import {
	logbookNavbar,
	NavbarMenuBar,
	type NavbarMenuConfig,
	type NavbarRendererProps,
	useHasPermission,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	COOLING_BED_PRODUCTION_LABEL,
	MILL_1,
	STACKER_PRODUCTION_LABEL,
} from "./mill-config/mill-config";
import {
	routeAdjustmentManager,
	routeAdjustmentReport,
} from "./routes/adjustment.routes";
import {
	routeCalendarYear,
	routeCalendarYearM1,
	routeCalendarYearM2,
} from "./routes/calendar-year.routes";
import {
	routeCalendar,
	routeCalendarM1,
	routeCalendarM2,
} from "./routes/calendar.routes";
import {
	routeArbor,
	routeCarrier,
	routeCassette,
	routeCassetteChock,
	routeChock,
	routeDonut,
	routeEdgerRoll,
	routeGuide,
	routeUniversalRoll,
} from "./routes/catalog.routes";
import {
	routeDelaysManager,
	routeDelaysPareto,
	routeDelaysReport,
	routeDelaysSankey,
} from "./routes/delay.routes";
import {
	routeIncentive,
	routeIncentiveManager,
} from "./routes/incentive.routes";
import {
	routeInspectionsManager,
	routeInspectionsReport,
} from "./routes/inspection.routes";
import {
	routeChemistriesReport,
	routeLabDashboard,
	routeLabTensileRequirements,
	routeLabTestsBySection,
} from "./routes/lab.routes";
import {
	routeM1Incentive,
	routeM1IncentiveManager,
	routeM1Overview,
	routeM1Schedule,
	routeM1ShiftReport,
	routeM1Gantt,
} from "./routes/m1.routes";
import {
	routeCarrierAssembly,
	routeCobblesManager,
	routeMillGantt,
	routeMillOverview,
	routeMillOverview2,
	routeMillSchedule,
	routeRejectedManager,
	routeUtilityConsumption,
	routeUtilityEngineering,
	routeUtilityManagement,
	utilityConsumptionPermission,
	utilityEngineeringPermission,
	utilityManagementPermission,
	utilityNavbarPermission,
} from "./routes/mill.routes";
import { routePartsManager } from "./routes/report.routes";
import {
	routeBrokenRolls,
	routeRollsets,
	routeRollshopNotifications,
} from "./routes/rollshop.routes";
import { routeSales } from "./routes/sales.routes";
import { routeShiftReport } from "./routes/shift_report.routes";
import { routeStackerProduction } from "./routes/stacker-pulpit.routes";

interface AreaAwareNavbarMenuConfig extends NavbarMenuConfig {
	defaultAreaAssetId?: number[];
}

const menuConfigData: AreaAwareNavbarMenuConfig[] = [
	{
		id: "rollshop",
		label: "Rollshop",
		items: [
			{
				label: "Rollsets",
				to: routeRollsets,
				permission: "ui.navbar.rollshop.rollsets",
			},
			{
				label: "Notifications",
				to: routeRollshopNotifications,
				permission: "ui.navbar.rollshop.notifications",
			},
			{
				label: "Broken Rolls",
				to: routeBrokenRolls,
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.rollshop",
	},
	{
		id: "mill",
		label: "Mill",
		items: [
			{
				label: "Carrier Status",
				to: routeCarrierAssembly,
				permission: "ui.navbar.mill.carrier.status",
			},
			{
				label: "Overview",
				to: routeMillOverview,
				permission: "ui.navbar.mill.overview",
			},
			{
				label: "Overview 2",
				to: routeMillOverview2,
				permission: "ui.navbar.mill.overview2",
			},
			{
				label: "Schedule",
				to: routeMillSchedule,
				permission: "ui.navbar.mill.schedule",
			},
			{
				label: "Cobbles Manager",
				to: routeCobblesManager,
				permission: "ui.navbar.mill.cobbles.manager",
			},
			{
				label: "Rejected Manager",
				to: routeRejectedManager,
				permission: "ui.navbar.mill.rejected.manager",
			},
			{
				label: "Gantt",
				to: routeMillGantt,
				permission: "ui.navbar.mill.gantt",
			},
			{
				label: "Incentive Dashboard",
				to: routeIncentive,
				permission: "ui.navbar.mill.incentive.dashboard",
			},
			{
				label: "Incentive Manager",
				to: routeIncentiveManager,
				permission: "ui.navbar.mill.incentive.manager",
			},
			{
				label: STACKER_PRODUCTION_LABEL,
				to: routeStackerProduction,
				permission: "ui.navbar.mill.stacker.production",
			},
			{
				label: "Shift Report",
				to: routeShiftReport,
				permission: "ui.navbar.shift.report",
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.mill",
	},
	{
		id: "m1-mill",
		label: "Mill",
		items: [
			{
				label: "Overview",
				to: routeM1Overview,
				permission: "ui.navbar.m1.mill.overview",
			},
			{
				label: "Schedule",
				to: routeM1Schedule,
				permission: "ui.navbar.m1.mill.schedule",
			},
			{
				label: "Incentive Dashboard",
				to: routeM1Incentive,
				permission: "ui.navbar.m1.incentive.dashboard",
			},
			{
				label: "Incentive Manager",
				to: routeM1IncentiveManager,
				permission: "ui.navbar.m1.incentive.manager",
			},
			{
				label: COOLING_BED_PRODUCTION_LABEL,
				to: routeStackerProduction,
				permission: "ui.navbar.m1.mill.stacker.production",
			},
			{
				label: "Shift Report",
				to: routeM1ShiftReport,
				permission: "ui.navbar.m1.shift.report",
			},
			{
				label: "Gantt",
				to: routeM1Gantt,
				permission: "ui.navbar.m1.gantt",
			},
		],
		defaultAreaAssetId: [MILL_1.id],
		permission: "ui.navbar.m1.mill",
	},
	{
		id: "utilities",
		label: "Utilities",
		items: [
			{
				label: "Management",
				to: routeUtilityManagement,
				permission: utilityManagementPermission,
			},
			{
				label: "Report",
				to: routeUtilityConsumption,
				permission: utilityConsumptionPermission,
			},
			{
				label: "Engineering",
				to: routeUtilityEngineering,
				permission: utilityEngineeringPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: utilityNavbarPermission,
	},
	{
		id: "yearview-sales",
		label: "Year View",
		items: [
			{
				label: "M1 Year View",
				to: routeCalendarYearM1,
			},
			{
				label: "M2 Year View",
				to: routeCalendarYearM2,
			},
		],
		permission: "ui.navbar.yearview.sales",
	},
	{
		id: "calendar-sales",
		label: "Calendar",
		items: [
			{
				label: "M1 Calendar",
				to: routeCalendarM1,
			},
			{
				label: "M2 Calendar",
				to: routeCalendarM2,
			},
		],
		permission: "ui.navbar.calendar.sales",
	},
	{
		id: "calendar",
		label: "Calendar",
		items: [
			{
				label: "Calendar",
				to: routeCalendar,
			},
			{
				label: "Year View",
				to: routeCalendarYear,
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.calendar",
	},
	{
		id: "sales",
		label: "Sales",
		to: routeSales,
		items: [],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.sales",
	},
	{
		id: "adjustments",
		label: "Adjustments",
		items: [
			{
				label: "Manager",
				to: routeAdjustmentManager,
				permission: "ui.navbar.adjustments.manager",
			},
			{
				label: "Report",
				to: routeAdjustmentReport,
				permission: "ui.navbar.adjustments.report",
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.adjustments",
	},
	{
		id: "delays",
		label: "Delays",
		items: [
			{
				label: "Manager",
				to: routeDelaysManager,
				permission: "ui.navbar.delays.manager",
			},
			{
				label: "Report",
				to: routeDelaysReport,
				permission: "ui.navbar.delays.report",
			},
			{
				label: "Pareto",
				to: routeDelaysPareto,
				permission: "ui.navbar.delays.pareto",
			},
			{
				label: "Sankey",
				to: routeDelaysSankey,
				permission: "ui.navbar.delays.sankey",
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.delays",
	},
	{
		id: "lab",
		label: "LAB",
		items: [
			{
				label: "Chemistries Report",
				to: routeChemistriesReport,
				permission: "ui.navbar.lab.chemistries.report",
			},
			//   {
			//     label: "Chemistries Dashboard",
			//     to: routeLabChemistries,
			//     permission: "ui.navbar.lab.chemistries",
			//   },
			{
				label: "Lab Dashboard",
				to: routeLabDashboard,
				permission: "ui.navbar.lab.dashboard",
			},
			{
				label: "Tests by Section",
				to: routeLabTestsBySection,
				permission: "ui.navbar.lab.tests.by.section",
			},
			{
				label: "Tensile Requirements",
				to: routeLabTensileRequirements,
				permission: "ui.navbar.lab.tensile.requirements",
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.lab",
	},
	{
		id: "inspections",
		label: "Inspections",
		items: [
			{
				label: "Manager",
				to: routeInspectionsManager,
				permission: "ui.navbar.inspections.manager",
			},
			{
				label: "Report",
				to: routeInspectionsReport,
				permission: "ui.navbar.inspections.report",
			},
		],
		defaultAreaAssetId: [MILL_1.id],
		permission: "ui.navbar.inspections",
	},
	{
		...logbookNavbar[0],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.logbook",
	},
	{
		id: "catalogs",
		label: "Catalogs",
		items: [
			{
				label: "Arbors",
				to: routeArbor,
				permission: "ui.navbar.catalogs.arbors",
			},
			{
				label: "Donuts",
				to: routeDonut,
				permission: "ui.navbar.catalogs.donuts",
			},
			{
				label: "Universal Rolls",
				to: routeUniversalRoll,
				permission: "ui.navbar.catalogs.universal.rolls",
			},
			{
				label: "Edger Rolls",
				to: routeEdgerRoll,
				permission: "ui.navbar.catalogs.edger.rolls",
			},
			{
				label: "Carriers",
				to: routeCarrier,
				permission: "ui.navbar.catalogs.carriers",
			},
			{
				label: "Cassettes",
				to: routeCassette,
				permission: "ui.navbar.catalogs.cassettes",
			},
			{
				label: "Chocks",
				to: routeChock,
				permission: "ui.navbar.catalogs.chocks",
			},
			{
				label: "Cassette Chocks",
				to: routeCassetteChock,
				permission: "ui.navbar.catalogs.cassette.chocks",
			},
			{
				label: "Guides",
				to: routeGuide,
				permission: "ui.navbar.catalogs.guides",
			},
			{
				label: "Parts Manager",
				to: routePartsManager,
				permission: "ui.navbar.parts",
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.catalogs",
	},
];

const CustomNavbar = (props: NavbarRendererProps = {}) => {
	const hasPermissionTo = useHasPermission();

	const {
		state: { defaultAreaId },
	} = useUserContext();

	const menus = menuConfigData.filter((menu) => {
		const isAreaAllowed =
			!menu.defaultAreaAssetId ||
			menu.defaultAreaAssetId.includes(Number(defaultAreaId));
		const hasPermission = menu.permission
			? hasPermissionTo(menu.permission)
			: true;
		return isAreaAllowed && hasPermission;
	});

	return <NavbarMenuBar menus={menus} {...props} />;
};

export { CustomNavbar };
